import { motion } from "framer-motion";

const team = [
  {
    name: "Janani",
    bio: "Janani leads Mononites as Managing Director. She oversees daily operations, builds lasting client relationships, and shapes the brand's identity. Her focus on menu creativity, food quality, and personal service ensures that every client receives not just food, but a warm dining experience rooted in authenticity and care.",
    stats: [
      { label: "Menus Crafted", value: "15+" },
      { label: "Client NPS", value: "98%" },
    ],
    bgAccent: "from-[oklch(0.28_0.09_265)] to-[oklch(0.22_0.07_265)]",
  },
  {
    name: "Sathiavel",
    bio: "Sakthi partners in the growth of Mononites, bringing years of experience in food service and kitchen management. He runs the day-to-day kitchen work, manages vendor relationships, and maintains the standards that define the Mononites experience. His hands-on style keeps quality consistent across every location and every meal served.",
    stats: [
      { label: "Locations Led", value: "3+" },
      { label: "Years in F&B", value: "10+" },
    ],
    bgAccent: "from-[oklch(0.26_0.09_265)] to-[oklch(0.22_0.07_265)]",
  },
];

export function Team() {
  return (
    <motion.section
      id="team"
      className="relative py-20 sm:py-28 md:py-32 overflow-hidden"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {/* Background glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full bg-[var(--gold)]/[0.04] blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.65 }}
          className="mb-12 sm:mb-16 text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <motion.span
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="h-px bg-gold block"
            />
            <span className="text-xs uppercase tracking-[0.4em] text-gold">The Directors</span>
            <motion.span
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="h-px bg-gold block"
            />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-6xl text-foreground">
            Faces behind the{" "}
            <span className="gradient-gold-text italic">flavor.</span>
          </h2>
          <p className="mt-5 text-muted-foreground text-base sm:text-lg font-light max-w-xl mx-auto">
            Two founders, one shared belief — that great food builds great workplaces.
          </p>
        </motion.div>

        {/* Landscape cards stacked vertically */}
        <div className="flex flex-col gap-5">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.7, delay: i * 0.18, ease: "easeOut" }}
              whileHover={{ y: -4, transition: { duration: 0.3 } }}
              className="group relative glass-card rounded-2xl sm:rounded-3xl overflow-hidden"
            >
              <div className="flex flex-col justify-center px-6 py-6 sm:px-8 sm:py-7">
                <motion.span
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.18 + 0.3 }}
                  className="inline-block w-fit px-2.5 py-1 rounded-full bg-gold/90 backdrop-blur-sm text-[var(--gold-foreground)] text-[9px] font-medium uppercase tracking-widest mb-4"
                >
                </motion.span>
                <h3 className="font-display text-2xl sm:text-3xl text-foreground group-hover:text-gold transition-colors duration-300">
                  {member.name}
                </h3>
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: 32 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.18 + 0.4 }}
                  className="h-px bg-gold mt-2 mb-4"
                />

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-light">
                  {member.bio}
                </p>
              </div>

              <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-gold to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" />
              <div className="absolute inset-0 bg-gradient-to-r from-gold/0 via-gold/[0.03] to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
